import {isEmpty} from 'lodash';
import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';

import {findGlobalOrigin} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {isDevelopment} from '../../../../libs/shared/helpers/src/environment/envHelpers';
import {FrameViewerState} from '../../../../srcCommon/inboxes/side/sideFrameActions';
import {buildIntegrationDataDiff, IntegrationDataSource} from '../../../helpers/integrationHelpers';
import {
  createStructuredSelector,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../helpers/reselectHelpers';
import {
  onTrackIntegrationMouseEvent,
  SidePanelFrameMouseTrackingValuesEnum,
} from '../../../helpers/sidePanelTrackingHelpers';
import {IntegrationModel} from '../../../models/api/integrationModel';
import {Dispatch} from '../../../store/appStore';
import {getCompany} from '../../../store/data/company/companySelectors';
import {getCustomFieldsForCardsFlat} from '../../../store/data/customFields/customFieldsSelectors';
import {getHost, getSideFrameIntegrations} from '../../../store/data/dataSelectors';
import {getPreferences} from '../../../store/data/preferences/preferencesSelectors';
import {getFlatActiveTeammates, getUser} from '../../../store/data/teammates/teammatesSelectors';
import {ViewerPanelId} from '../../../store/panels/panelStateBase';
import {Iframe} from '../../common/iframe';
import {useFrameOnMessage, useWasVisibleOnce} from './sideFrameHooks';

/*
 * Props.
 */

export interface IntegrationFrameContainerProps {
  panelId: ViewerPanelId;
  viewerState?: FrameViewerState;
}

type IntegrationFrameContainerStateProps = ResolveStateProps<typeof mapStateToProps>;

interface IntegrationFrameContainerComponentProps
  extends IntegrationFrameContainerProps,
    IntegrationFrameContainerStateProps {
  dispatch: Dispatch;
  integration?: IntegrationModel;
  isVisible?: boolean;
}

/*
 * Component.
 */

const IntegrationFrameContainerComponent: FC<IntegrationFrameContainerComponentProps> = (props) => {
  /*
   * Integration communication.
   */

  const {host, integration, isVisible, integrationData} = props;
  const [isReady, setIsReady] = useState(false);
  const origin = useMemo(() => (isDevelopment() ? host : findGlobalOrigin()), [host]);

  const {isConnected, onMessage, sendDataToIframe, dialogs} = useFrameOnMessage({
    ...props,
    isReady,
    targetOrigin: origin,
  });

  /*
   * Send events when core models are updated.
   */

  const lastIntegrationDataRef = useRef<IntegrationDataSource>();
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    // Keep track of the previous value.
    const previousIntegrationData = lastIntegrationDataRef.current;
    lastIntegrationDataRef.current = integrationData;

    // Create a diff of the integration data, and send to the frame.
    const integrationDataDiff = buildIntegrationDataDiff(previousIntegrationData, integrationData);
    if (isEmpty(integrationDataDiff)) {
      return;
    }

    sendDataToIframe({
      type: 'update_data',
      val: [integrationDataDiff],
    });

    setIsReady(true);
  }, [integrationData, isConnected, sendDataToIframe]);

  const integrationType = integration && integration.type;
  useEffect(() => {
    sendDataToIframe({
      type: 'switch_panel',
      val: [integrationType || null],
    });
  }, [sendDataToIframe, integrationType]);

  /*
   * Tracking events.
   */

  const onMouseTrack = (type: SidePanelFrameMouseTrackingValuesEnum) => {
    if (!integration) {
      return;
    }
    onTrackIntegrationMouseEvent(type, integration.name, {
      integration_id: integration.id,
    });
  };

  /*
   * Render.
   */

  // No need to render the iFrame if we weren't visible at least once.
  const wasVisibleOnce = useWasVisibleOnce(props.isVisible);
  if (!wasVisibleOnce) {
    return null;
  }

  const frameEndpoint = `${origin}/plugins2`;
  const integrationName = (integration && integration.name) || '';
  return (
    <>
      {dialogs}
      <Iframe
        isVisible={isVisible}
        endpoint={frameEndpoint}
        title={integrationName}
        onMessage={onMessage}
        onMouseEnter={() => onMouseTrack(SidePanelFrameMouseTrackingValuesEnum.MOUSE_ENTER)}
        onMouseLeave={() => onMouseTrack(SidePanelFrameMouseTrackingValuesEnum.MOUSE_LEAVE)}
      />
    </>
  );
};

/*
 * Selectors.
 */

const getIntegrationData = createStructuredSelector({
  company: getCompany,
  user: getUser,
  preferences: getPreferences,
  teammates: getFlatActiveTeammates,
  integrations: getSideFrameIntegrations,
  customFields: getCustomFieldsForCardsFlat,
});

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  host: getHost,
  integrationData: getIntegrationData,
}));

export const IntegrationFrameContainer = connect(mapStateToProps)(IntegrationFrameContainerComponent);
